/**
 * Paleta y utilidades para MemberRole (cargos en club).
 *
 * Coherente con la paleta de secciones (`sections.ts`): usa tokens neutros
 * (indigo, slate, emerald, amber, rose, cyan) para no competir con los
 * colores oficiales de sección. La combinación final se hace en `<RoleBadge>`
 * mezclando color de sección (chip) + color de rol (etiqueta interior).
 */

import type { MemberRole } from "@/types";
import {
  Crown,
  Star,
  Users,
  BookOpen,
  Wallet,
  ClipboardList,
  UserRound,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

/** Nivel jerárquico: menor = mayor jerarquía. Mismo orden que en memberRoles. */
export const ROLE_RANK: Record<MemberRole, number> = {
  director: 1,
  subdirector: 2,
  secretario: 3,
  tesorero: 4,
  anciano: 5,
  consejero: 6,
  instructor: 7,
  member: 99,
};

/** Chip sólido (fondo lleno) — usar en badges principales sin sección. */
export const ROLE_CHIP_CLASSES: Record<MemberRole, string> = {
  director: "bg-indigo-600 text-white",
  subdirector: "bg-indigo-500 text-white",
  secretario: "bg-rose-500 text-white",
  tesorero: "bg-amber-500 text-white",
  anciano: "bg-purple-500 text-white",
  consejero: "bg-emerald-500 text-white",
  instructor: "bg-cyan-500 text-white",
  member: "bg-slate-400 text-white",
};

/** Chip suave (fondo pastel + texto oscuro) — para tablas y filtros discretos. */
export const ROLE_CHIP_SOFT_CLASSES: Record<MemberRole, string> = {
  director: "bg-indigo-100 text-indigo-800 border border-indigo-200",
  subdirector: "bg-indigo-50 text-indigo-700 border border-indigo-200",
  secretario: "bg-rose-50 text-rose-700 border border-rose-200",
  tesorero: "bg-amber-50 text-amber-800 border border-amber-200",
  anciano: "bg-purple-50 text-purple-800 border border-purple-200",
  consejero: "bg-emerald-50 text-emerald-800 border border-emerald-200",
  instructor: "bg-cyan-50 text-cyan-800 border border-cyan-200",
  member: "bg-slate-100 text-slate-700 border border-slate-200",
};

/** HEX de acento para gráficos/badges dinámicos. */
export const ROLE_ACCENT_HEX: Record<MemberRole, string> = {
  director: "#4F46E5",
  subdirector: "#6366F1",
  secretario: "#F43F5E",
  tesorero: "#F59E0B",
  anciano: "#A855F7",
  consejero: "#10B981",
  instructor: "#06B6D4",
  member: "#94A3B8",
};

/** Icono por rol para reforzar visualmente. */
export const ROLE_ICONS: Record<MemberRole, LucideIcon> = {
  director: Crown,
  subdirector: Star,
  secretario: ClipboardList,
  tesorero: Wallet,
  anciano: ShieldCheck,
  consejero: Users,
  instructor: BookOpen,
  member: UserRound,
};

/** Ordena roles por jerarquía (director primero). */
export function sortRoles<T extends MemberRole>(roles: readonly T[]): T[] {
  return [...roles].sort((a, b) => ROLE_RANK[a] - ROLE_RANK[b]);
}

/**
 * Reglas de negocio: qué combinaciones (rol × sección) NO son válidas.
 * - Guías Mayores no tiene consejeros: todos los adultos son miembros de GM
 *   y ejercen el consejería en Aventureros o Conquistadores (o son
 *   instructores).
 */
export function isRoleAllowedInSection(
  role: MemberRole,
  section: string,
): boolean {
  if (role === "consejero" && section === "guias_mayores") return false;
  return true;
}

/** Mensaje explicativo cuando la combinación no está permitida. */
export function roleSectionRestrictionMessage(
  role: MemberRole,
  section: string,
): string | null {
  if (role === "consejero" && section === "guias_mayores") {
    return "Guías Mayores no tiene consejeros. Los adultos son miembros de GM y ejercen la consejería en Aventureros o Conquistadores. Usa 'Instructor' si acompaña actividades de GM.";
  }
  return null;
}
